// Classifies failures from auth0-spa-js's getAccessTokenSilently.
//
// Kept free of any import that touches `window` — `src/config.ts` reads `window.config` at module
// load, and jest runs with testEnvironment: 'node', so pulling config in here would make this
// untestable. `isOnline` is passed in for the same reason.
//
// Every auth0-spa-js error extends GenericError and carries `.error` / `.error_description`, so
// reading `.error` covers OAuthError, MissingRefreshTokenError, TimeoutError and the rest.

export type AuthFailureKind = 'terminal' | 'transient' | 'unknown';

export interface AuthFailure {
  kind: AuthFailureKind;
  code: string;
  description: string;
}

// The session is gone and no amount of retrying in the background will bring it back — only the
// user going through Auth0 again will.
export const TERMINAL_CODES = [
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token',
  'unauthorized',
  'access_denied',
];

// Worth retrying: the session may well be fine and the network wasn't.
const TRANSIENT_CODES = ['timeout', 'request_error'];

export function classifyAuthError(error: any, isOnline = true): AuthFailure {
  const code: string = error?.error || error?.code || 'unknown';
  const description: string = error?.error_description || error?.message || 'No description';

  if (TERMINAL_CODES.includes(code)) {
    return { kind: 'terminal', code, description };
  }

  if (TRANSIENT_CODES.includes(code)) {
    return { kind: 'transient', code, description };
  }

  // ApiBase assigns this name to network failures, and an offline browser can't do better than
  // wait — neither says anything about whether the session is still valid.
  if (error?.name === 'NetworkError' || error?.name === 'TimeoutError' || !isOnline) {
    return { kind: 'transient', code, description };
  }

  // A code we've never seen is retried like a transient one, but the caller caps the attempts so
  // a novel code can't wedge the app in a retry loop.
  return { kind: 'unknown', code, description };
}

export const MAX_TOKEN_REFRESH_ATTEMPTS = 4;

// 5s, 30s, 2m, 5m. Anything past the cap is the caller's signal to give up and treat the failure
// as terminal.
const BACKOFF_SECONDS = [5, 30, 120, 300];

export function backoffSeconds(attempt: number) {
  const index = Math.min(Math.max(attempt, 0), BACKOFF_SECONDS.length - 1);
  return BACKOFF_SECONDS[index];
}
